
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { postData } from '../../services/FetchNodeServices.js';

let persistentSidebarScrollTop = 0;
import SignOutConfirmModal from './SignOutCOnfirmModal';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { MODULES } from '@/utils/permissions';

const API_BASE = `${import.meta.env.VITE_API_URL}/api/v1`;

interface NavItem {
  label: string;
  path: string;
  icon: string;
  items?: NavItem[];
}
interface NavGroup {
  label: string;
  icon: string;
  basePath: string;
  items: NavItem[];
}
interface CompanyData {
  name: string;
  logo_url?: string | null;
}

const mastersGroup: NavGroup = {
  label: 'Masters',
  icon: 'ri-database-2-line',
  basePath: '/masters',
  items: [
    {
      label: 'Warehouses',
      path: '/masters/warehouses',
      icon: 'ri-store-3-line',
    },
    { label: 'Parties', path: '/masters/parties', icon: 'ri-group-2-line' },
    {
      label: 'Categories & Units',
      path: '/masters/categories',
      icon: 'ri-price-tag-3-line',
    },
    { label: 'Items', path: '/masters/items', icon: 'ri-box-3-line' },
    { label: 'Shifts', path: '/masters/shifts', icon: 'ri-time-line' },
    {
      label: 'Operators',
      path: '/masters/operators',
      icon: 'ri-user-settings-line',
    },
    {
      label: 'Machines',
      path: '/masters/machines',
      icon: 'ri-settings-3-line',
    }, 
    {
      label: 'Cost Centers',
      path: '/masters/cost-centers',
      icon: 'ri-building-4-line',
    },  
    {
      label: 'Work Centers',
      path: '/masters/work-centers',
      icon: 'ri-building-2-line',
    },   
    
    {
      label: 'Rejection Codes',
      path: '/masters/rejection-codes',
      icon: 'ri-close-circle-line',
    },
    {
      label: 'Downtime Codes',
      path: '/masters/downtime-codes',
      icon: 'ri-timer-flash-line',
    },
    {
      label: 'Quality Parameters',
      path: '/masters/quality-parameters',
      icon: 'ri-microscope-line',
    },
    {
      label: 'Inspection Checklists',
      path: '/masters/inspection-checklists',
      icon: 'ri-checkbox-multiple-line',
    },
  ],
};

const salesGroup: NavGroup = {
  label: 'Sales',
  icon: 'ri-shopping-cart-2-line',
  basePath: '/sales',
  items: [
    { label: 'Quotations', path: '/sales/quotations', icon: 'ri-file-text-line' },
    { label: 'Proforma Invoices', path: '/sales/proforma', icon: 'ri-bill-line' },
    { label: 'Sales Orders', path: '/sales/orders', icon: 'ri-file-list-3-line' },
    { label: 'Invoices', path: '/sales/invoices', icon: 'ri-file-text-line' },
    {
      label: 'Payments',
      path: '/sales/payments',
      icon: 'ri-money-rupee-circle-line',
    },
    {
      label: 'Sale Returns',
      path: '/sales/returns',
      icon: 'ri-arrow-go-back-line',
    },
    { label: 'Challans', path: '/sales/challans', icon: 'ri-truck-line' },
  ],
};

const purchaseGroup: NavGroup = {
  label: 'Purchase',
  icon: 'ri-store-2-line',
  basePath: '/purchase',
  items: [
    {
      label: 'Purchase Orders',
      path: '/purchase/orders',
      icon: 'ri-file-list-3-line',
    },
    {
      label: 'PO Approvals',
      path: '/purchase/approvals',
      icon: 'ri-shield-check-line'
    },
    {
      label: 'Invoices',
      path: '/purchase/invoices',
      icon: 'ri-file-text-line',
    },
    {
      label: 'Payments',
      path: '/purchase/payments',
      icon: 'ri-money-rupee-circle-line',
    },
    {
      label: 'GRN History',
      path: '/purchase/grn',
      icon: 'ri-inbox-archive-line',
    },
    {
      label: 'Purchase Returns',
      path: '/purchase/returns',
      icon: 'ri-arrow-go-forward-line',
    },
  ],
};

// compliance tab
const complianceGroup: NavGroup = {
  label: 'Compliance', icon: 'ri-file-text-line', basePath: '/compliance',
  items: [
    { label: 'E-Way Bill', path: '/compliance/eway-bill', icon: 'ri-truck-line' },
    { label: 'E-Invoice', path: '/compliance/e-invoice', icon: 'ri-qr-code-line' },
    { label: 'TDS / TCS', path: '/compliance/tds', icon: 'ri-percent-line' },
  ],
};

const inventoryGroup: NavGroup = {
  label: 'Inventory',
  icon: 'ri-archive-stack-line',
  basePath: '/inventory',
  items: [
    {
      label: 'Stock View',
      path: '/inventory/stock',
      icon: 'ri-bar-chart-horizontal-line',
    },
    {
      label: 'Stock Receiving',
      path: '/inventory/receiving',
      icon: 'ri-inbox-archive-line',
    },
    {
      label: 'Stock Entries',
      path: '/inventory/stock-entries',
      icon: 'ri-add-box-line',
    },
    {
      label: 'Serial Numbers',
      path: '/inventory/serials',
      icon: 'ri-qr-code-line',
    },
    {
      label: 'Serial Traceability',
      path: '/inventory/serial-tracking',
      icon: 'ri-route-line',
    },
    {
      label: 'Transfers',
      path: '/inventory/transfer',
      icon: 'ri-swap-box-line',
    },
    {
      label: 'Adjustments',
      path: '/inventory/adjustment',
      icon: 'ri-equalizer-line',
    },
    {
      label: 'Scrap',
      path: '/inventory/scrap-management',
      icon: 'ri-delete-bin-6-line',
      items: [
        {
          label: 'Scrap Management',
          path: '/inventory/scrap-management',
          icon: 'ri-file-list-3-line',
        },
        {
          label: 'Scrap Yard',
          path: '/inventory/scrap-yard',
          icon: 'ri-inbox-archive-line',
        },
      ],
    },
    {
      label: 'Outward Gate Pass',
      path: '/inventory/gate-pass/outward',
      icon: 'ri-logout-box-r-line',
    },
    {
      label: 'Inward Gate Pass',
      path: '/inventory/gate-pass/inward',
      icon: 'ri-login-box-line',
    },
  ],
};

const printGroup: NavGroup = {
  label: 'Print',
  icon: 'ri-printer-line',
  basePath: '/print',
  items: [
    {
      label: 'Barcode Print',
      path: '/print/barcode-management',
      icon: 'ri-barcode-line',
    },
  ],
};

// manufacturing tab 
const manufacturingGroup: NavGroup = {
  label: 'Manufacturing',
  icon: 'ri-tools-line',
  basePath: '/manufacturing',
  items: [
    // The operator's own assigned work (operator linked to the login)
    // {
    //   label: 'My Work Orders',
    //   path: '/manufacturing/my-work-orders',
    //   icon: 'ri-user-star-line',
    // },
    {
      label: 'Bill of Materials',
      path: '/manufacturing/bom',
      icon: 'ri-git-branch-line',
      items: [
        {
          label: 'BOM List',
          path: '/manufacturing/bom-list',
          icon: 'ri-list-check',
        },
        {
          label: 'Create BOM',
          path: '/manufacturing/create-bom',
          icon: 'ri-add-line',
        },
      ],
    },
    {
      label: 'Routing',
      path: '/masters/routing',
      icon: 'ri-route-line',
      items: [
        {
          label: 'Routing List',
          path: '/masters/routing',
          icon: 'ri-list-check',
        },
        {
          label: 'Create Routing',
          path: '/masters/routing/new',
          icon: 'ri-add-line',
        },
      ],
    },
    {
      label: 'Production',
      path: '/manufacturing/production',
      icon: 'ri-stack-line',
      items: [
        {
          label: 'Production Orders',
          path: '/manufacturing/production-orders',
          icon: 'ri-stack-line',
        },
        {
          label: 'Work Orders',
          path: '/manufacturing/work-orders',
          icon: 'ri-tools-line',
        },
        {
          label: 'Material Issue Queue',
          path: '/manufacturing/issue-queue',
          icon: 'ri-inbox-unarchive-line',
        },
        {
          label: 'Schedule',
          path: '/manufacturing/schedule',
          icon: 'ri-calendar-line',
        },
      ],
    },
    {
      label: 'MRP',
      path: '/manufacturing/mrp',
      icon: 'ri-instance-line',
      items: [
        {
          label: 'Run MRP',
          path: '/manufacturing/mrp',
          icon: 'ri-instance-line',
        },
        {
          label: 'Material Shortage',
          path: '/manufacturing/mrp/shortage',
          icon: 'ri-error-warning-line',
        },
        {
          label: 'Purchase Suggest',
          path: '/manufacturing/mrp/purchase',
          icon: 'ri-shopping-cart-line',
        },
      ],
    },
    {
      label: 'Shop Floor',
      path: '/manufacturing/shop-floor',
      icon: 'ri-building-4-line',
      items: [
        {
          label: 'Live Dashboard',
          path: '/manufacturing/live',
          icon: 'ri-layout-grid-line',
        },
        {
          label: 'Production Entry',
          path: '/manufacturing/shop-floor',
          icon: 'ri-building-4-line',
        },
        {
          label: 'Downtime Entry',
          path: '/manufacturing/downtime',
          icon: 'ri-time-line',
        },
      ],
    },
    {
      label: 'Quality',
      path: '/manufacturing/quality',
      icon: 'ri-award-line',
      items: [
        {
          label: 'Incoming QC',
          path: '/manufacturing/qc/incoming',
          icon: 'ri-shield-check-line',
        },
        {
          label: 'In-Process QC',
          path: '/manufacturing/qc/inprocess',
          icon: 'ri-shield-check-line',
        },
        {
          label: 'Final QC',
          path: '/manufacturing/qc/final',
          icon: 'ri-award-line',
        },
        {
          label: 'NCR / CAPA',
          path: '/manufacturing/qc/ncr',
          icon: 'ri-file-damage-line',
        },
      ],
    },
    // ✅ ADD THIS - COSTING
    {
      label: 'Costing',
      path: '/manufacturing/costing',
      icon: 'ri-money-rupee-circle-line',
      items: [
        {
          label: 'Costing Dashboard',
          path: '/manufacturing/costing',
          icon: 'ri-dashboard-line',
        },
        {
          label: 'Production Order Cost',
          path: '/manufacturing/costing/orders',
          icon: 'ri-calculator-line',
        },
        {
          label: 'Standard Cost',
          path: '/manufacturing/costing/standard',
          icon: 'ri-money-rupee-circle-line',
        },
        {
          label: 'Actual Cost',
          path: '/manufacturing/costing/actual',
          icon: 'ri-coins-line',
        },
        {
          label: 'Variance Report',
          path: '/manufacturing/costing/variance',
          icon: 'ri-bar-chart-2-line',
        }
      ],
    },
    // Job Work
    {
      label: 'Job Work',
      path: '/manufacturing/jobwork',
      icon: 'ri-tools-line',
      items: [
        {
          label: 'All Orders',
          path: '/manufacturing/jobwork',
          icon: 'ri-file-list-3-line',
        },
        {
          label: 'New Job Work',
          path: '/manufacturing/jobwork/new',
          icon: 'ri-add-circle-line',
        },
      ],
    },
    // Batch Traceability
    {
      label: 'Batch Traceability',
      path: '/manufacturing/traceability',
      icon: 'ri-git-branch-line',
      items: [
        {
          label: 'Traceability',
          path: '/manufacturing/traceability',
          icon: 'ri-dashboard-line',
        },
        {
          label: 'Forward Trace',
          path: '/manufacturing/traceability/forward',
          icon: 'ri-arrow-right-circle-line',
        },
        {
          label: 'Backward Trace',
          path: '/manufacturing/traceability/backward',
          icon: 'ri-arrow-left-circle-line',
        },
        {
          label: 'Recall Simulation',
          path: '/manufacturing/traceability/recall',
          icon: 'ri-alert-line',
        },
      ],
    },
    // Tool & Die
    {
      label: 'Tool & Die',
      path: '/manufacturing/tools',
      icon: 'ri-settings-4-line',
      items: [
        {
          label: 'All Tools',
          path: '/manufacturing/tools',
          icon: 'ri-file-list-3-line',
        },
        {
          label: 'Add Tool',
          path: '/manufacturing/tools/new',
          icon: 'ri-add-circle-line',
        },
      ],
    },
    // Maintenance
    {
      label: 'Maintenance',
      path: '/manufacturing/maintenance',
      icon: 'ri-tools-line',
      items: [
        {
          label: 'Dashboard',
          path: '/manufacturing/maintenance',
          icon: 'ri-dashboard-line',
        },
        {
          label: 'Work Orders',
          path: '/manufacturing/maintenance/workorders',
          icon: 'ri-file-list-3-line',
        },
        {
          label: 'Schedules',
          path: '/manufacturing/maintenance/schedules',
          icon: 'ri-calendar-check-line',
        },
        {
          label: 'Calendar',
          path: '/manufacturing/maintenance/calendar',
          icon: 'ri-calendar-line',
        },
      ],
    },
  ],
};

// finance tab
const financeGroup: NavGroup = {
  label: 'Finance',
  icon: 'ri-money-rupee-circle-line',
  basePath: '/finance',
  items: [
    {
      label: 'Chart of Accounts',
      path: '/finance/accounts',
      icon: 'ri-git-branch-line',
    },
    {
      label: 'Journal Vouchers',
      path: '/finance/journal',
      icon: 'ri-file-text-line',
    },
    {
      label: 'Trial Balance',
      path: '/finance/trial-balance',
      icon: 'ri-scales-3-line',
    },
    {
      label: 'Ledger',
      path: '/finance/ledger',
      icon: 'ri-book-open-line',
    },
    {
      label: 'Profit & Loss',
      path: '/finance/reports/profit-loss',
      icon: 'ri-line-chart-line',
    },
    {
      label: 'Balance Sheet',
      path: '/finance/reports/balance-sheet',
      icon: 'ri-funds-box-line',
    },
    {
      label: 'Bank Reconciliation',
      path: '/finance/bank-reconciliation',
      icon: 'ri-bank-line',
    },
    {
      label: 'Fixed Assets',
      path: '/finance/assets',
      icon: 'ri-building-line',
    },
    {
      label: 'Currencies',
      path: '/finance/currencies',
      icon: 'ri-coin-line',
    },
  ],
};

const reportsGroup: NavGroup = {
  label: 'Reports',
  icon: 'ri-bar-chart-2-line',
  basePath: '/reports',
  items: [
    {
      label: 'Stock Summary',
      path: '/reports/stock-summary',
      icon: 'ri-stack-line',
    },
    {
      label: 'Stock Ledger',
      path: '/reports/stock-ledger',
      icon: 'ri-file-list-3-line',
    },
    {
      label: 'Low Stock Alert',
      path: '/reports/low-stock',
      icon: 'ri-alert-line',
    },
    {
      label: 'Purchase Register',
      path: '/reports/purchase-register',
      icon: 'ri-store-2-line',
    },
    {
      label: 'GST Purchase',
      path: '/reports/gst-purchase',
      icon: 'ri-receipt-line',
    },
    {
      label: 'Sales Register',
      path: '/reports/sales-register',
      icon: 'ri-bar-chart-box-line',
    },
    {
      label: 'GST Sales (GSTR-1)',
      path: '/reports/gst-sales',
      icon: 'ri-file-shield-2-line',
    },
    {
      label: 'Outstanding',
      path: '/reports/outstanding',
      icon: 'ri-money-rupee-circle-line',
    },
    {
      label: 'Day Book',
      path: '/reports/day-book',
      icon: 'ri-calendar-check-line',
    },
    {
      label: 'Party Ledger',
      path: '/reports/party-ledger',
      icon: 'ri-group-line',
    },
    {
      label: 'Manufacturing Reports',
      path: '/reports/manufacturing',
      icon: 'ri-settings-3-line',
    },
    {
      label: 'Scrap Report',
      path: '/reports/scrap',
      icon: 'ri-delete-bin-6-line',
    },
    {
      label: 'Financial Reports',
      path: '/reports/financial',
      icon: 'ri-funds-line',
    },
    {
      label: 'GST Reports',
      path: '/reports/gst',
      icon: 'ri-file-shield-line',
    },
    {
      label: 'Executive Dashboard',
      path: '/reports/dashboard',
      icon: 'ri-dashboard-3-line',
    },
    {
      label: 'AI Forecasting',
      path: '/reports/ai-forecasting',
      icon: 'ri-magic-line',
    },
  ],
};

const guardNavItems: NavItem[] = [
  { label: 'Dashboard', path: '/guard/dashboard', icon: 'ri-shield-user-line' },
  {
    label: 'Outward Passes',
    path: '/guard/outward',
    icon: 'ri-logout-box-r-line',
  },
  { label: 'Inward Passes', path: '/guard/inward', icon: 'ri-login-box-line' },
];

const roleLabel: Record<string, string> = {
  SUPER_ADMIN: 'Super Admin',
  SUB_ADMIN: 'Manager',
  COMPANY_ADMIN: 'Company Admin',
  END_USER: 'Staff',
  SECURITY_GUARD: 'Security Guard',
};

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const { user, hasPermission, hasControl, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const navRef = useRef<HTMLElement>(null);

  const handleNavScroll = () => {
    if (navRef.current) {
      persistentSidebarScrollTop = navRef.current.scrollTop;
    }
  };

  useLayoutEffect(() => {
    if (navRef.current) {
      navRef.current.scrollTop = persistentSidebarScrollTop;
    }
  });

  // ── All hooks at the top — no conditional hooks ───────────────────────────
  const toast = useToast();
  const [mastersOpen, setMastersOpen] = useState(
    location.pathname.startsWith('/masters'),
  );
  const [salesOpen, setSalesOpen] = useState(
    location.pathname.startsWith('/sales'),
  );
  const [purchaseOpen, setPurchaseOpen] = useState(
    location.pathname.startsWith('/purchase'),
  );
  const [complianceOpen, setComplianceOpen] = useState(location.pathname.startsWith('/compliance'));
  const [inventoryOpen, setInventoryOpen] = useState(
    location.pathname.startsWith('/inventory'),
  );
  const [printOpen, setPrintOpen] = useState(
    location.pathname.startsWith('/print'),
  );
  const [manufacturingOpen, setManufacturingOpen] = useState(
    location.pathname.startsWith('/manufacturing') || location.pathname.startsWith('/masters/routing'),
  );
  const [financeOpen, setFinanceOpen] = useState(location.pathname.startsWith('/finance'));

  const [reportsOpen, setReportsOpen] = useState(
    location.pathname.startsWith('/reports'),
  );
  const [logoutConfirm, setLogoutConfirm] = useState(false);
  const [company, setCompany] = useState<CompanyData | null>(null);
  const [eInvoiceEligible, setEInvoiceEligible] = useState<boolean>(true);

  const fetchCompanyData = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    fetch(`${API_BASE}/company/get`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (json?.data) {
          const d = json.data;
          setCompany({
            name: d?.name ?? '',
            logo_url: d?.logo_url ?? null,
          });

          // Check ₹5 Crore turnover threshold (50,000,000) or manual override
          const prevTurnover = Number(d?.previous_fy_turnover || 0);
          const isEligible = prevTurnover >= 50000000 ||
            Boolean(d?.einvoice_applicable) ||
            Boolean(d?.einvoice_manual_override);
          setEInvoiceEligible(isEligible);
        }
      })
      .catch(() => { });
  }, []);

  useEffect(() => {
    fetchCompanyData();

    const handleCompanyUpdate = (e: any) => {
      if (e?.detail?.previous_fy_turnover !== undefined) {
        const prevTurnover = Number(e.detail.previous_fy_turnover || 0);
        const isEligible = prevTurnover >= 50000000 || Boolean(e.detail.einvoice_manual_override);
        setEInvoiceEligible(isEligible);
      }
      fetchCompanyData();
    };

    window.addEventListener('company-updated', handleCompanyUpdate);
    window.addEventListener('focus', fetchCompanyData);
    return () => {
      window.removeEventListener('company-updated', handleCompanyUpdate);
      window.removeEventListener('focus', fetchCompanyData);
    };
  }, [fetchCompanyData]);

  // ── Derived values ────────────────────────────────────────────────────────
  const role = user?.role ?? '';
  const isGuard = role === 'SECURITY_GUARD';

  const userInitials = (user?.name ?? 'AU')
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  // ── Permission-filtered items ─────────────────────────────────────────────
  const visibleMastersItems = mastersGroup.items.filter((item) => {
    if (item.path === '/masters/warehouses')
      return hasPermission(MODULES.WAREHOUSES, 'view');
    if (item.path === '/masters/parties')
      return hasPermission(MODULES.PARTIES, 'view');
    if (item.path === '/masters/items')
      return hasPermission(MODULES.ITEMS, 'view');
    if (item.path === '/masters/categories')
      return hasPermission(MODULES.CATEGORIES, 'view');
    if (item.path === '/masters/work-centers')
      return hasPermission(MODULES.WORK_CENTERS, 'view');
    if (item.path === '/masters/machines')
      return hasPermission(MODULES.MACHINE, 'view');
    if (item.path === '/masters/shifts')
      return hasPermission(MODULES.SHIFT, 'view');
    if (item.path === '/masters/operators')
      return hasPermission(MODULES.OPERATOR, 'view');
    if (item.path === '/masters/cost-centers')
      return hasPermission(MODULES.COST_CENTRE, 'view');
    if (item.path === '/masters/rejection-codes')
      return hasPermission(MODULES.REJECTION_CODE, 'view');
    if (item.path === '/masters/downtime-codes')
      return hasPermission(MODULES.DOWNTIME_CODE, 'view');
    if (item.path === '/masters/quality-parameters')
      return hasPermission(MODULES.QUALITY_PARAMETER, 'view');
    if (item.path === '/masters/inspection-checklists')
      return hasPermission(MODULES.INSPECTION_CHECKLIST, 'view');
    if (item.path === '/masters/routing')
      return hasPermission(MODULES.ROUTING, 'view');
    if (item.path === '/masters/routing/new')
      return hasPermission(MODULES.ROUTING, 'create');
    return false; // default: hide any unrecognised masters items
  });

  const visibleSalesItems = salesGroup.items.filter((item) => {
    if (item.path === '/sales/quotations')
      return hasPermission(MODULES.SALES_INVOICE, 'view');
    if (item.path === '/sales/proforma')
      return hasPermission(MODULES.SALES_INVOICE, 'view');
    if (item.path === '/sales/orders')
      return hasPermission(MODULES.SALES_INVOICE, 'view');
    if (item.path === '/sales/invoices')
      return hasPermission(MODULES.SALES_INVOICE, 'view');
    if (item.path === '/sales/payments')
      return hasPermission(MODULES.SALES_PAYMENT, 'view');
    if (item.path === '/sales/returns')
      return hasPermission(MODULES.SALE_RETURN, 'view');
    if (item.path === '/sales/challans')
      return hasPermission(MODULES.CHALLAN, 'view');
    return false;
  });

  const visiblePurchaseItems = purchaseGroup.items.filter((item) => {
    if (item.path === '/purchase/orders')
      return hasPermission(MODULES.PURCHASE_ORDER, 'view');
    if (item.path === '/purchase/approvals')
      return hasPermission(MODULES.PURCHASE_APPROVAL, 'view');
    if (item.path === "/purchase/approvals/settings")
      return hasPermission(MODULES.PURCHASE_APPROVAL, 'view')
    if (item.path === '/purchase/invoices')
      return hasPermission(MODULES.PURCHASE_INVOICE, 'view');
    if (item.path === '/purchase/payments')
      return hasPermission(MODULES.PURCHASE_PAYMENT, 'view');
    if (item.path === '/purchase/grn')
      return hasPermission(MODULES.GRN_HISTORY, 'view');
    if (item.path === '/purchase/returns')
      return hasPermission(MODULES.PURCHASE_RETURN, 'view');
    return false;
  });

  const visibleInventoryItems = inventoryGroup.items.filter((item) => {
    if (item.path === '/inventory/stock')
      return (
        hasPermission(MODULES.STOCK_ADJUSTMENT, 'view') ||
        hasControl('approveStockAdjustment')
      );
    if (item.path === '/inventory/receiving')
      return hasPermission(MODULES.STOCK_RECEIVING, 'create');
    if (item.path === '/inventory/stock-entries')
      return hasPermission(MODULES.STOCK_ENTRIES, 'view');
    if (item.path === '/inventory/transfer')
      return hasPermission(MODULES.STOCK_TRANSFER, 'view');
    if (item.path === '/inventory/adjustment')
      return (
        hasPermission(MODULES.STOCK_ADJUSTMENT, 'view') ||
        hasControl('approveStockAdjustment')
      );
    if (item.path === '/inventory/gate-pass/outward')
      return hasPermission(MODULES.GATE_PASS_OUTWARD, 'view');
    if (item.path === '/inventory/gate-pass/inward')
      return hasPermission(MODULES.GATE_PASS_INWARD, 'view');
    if (item.path === '/inventory/serials' || item.path === '/inventory/serial-tracking')
      return true;
    if (item.path === '/inventory/scrap-management')
      return true;
    return false;
  });

  const visibleComplianceItems = complianceGroup.items.filter((item) => {
    if (item.path === '/compliance/eway-bill')
      return hasPermission(MODULES.EWAY_BILL, 'view');
    if (item.path === '/compliance/e-invoice')
      return hasPermission(MODULES.E_INVOICE, 'view');
    if (item.path === '/compliance/tds')
      return hasPermission(MODULES.TDS, 'view');
    return false;
  });

  const visibleReportsItems = reportsGroup.items.filter((item) => {
    const fin = hasControl('viewFinancialReports');
    if (item.path === '/reports/stock-summary')
      return hasPermission(MODULES.REPORT_STOCK_SUMMARY, 'view') || fin;
    if (item.path === '/reports/stock-ledger')
      return hasPermission(MODULES.REPORT_STOCK_LEDGER, 'view') || fin;
    if (item.path === '/reports/low-stock')
      return hasPermission(MODULES.REPORT_LOW_STOCK, 'view') || fin;
    if (item.path === '/reports/purchase-register')
      return hasPermission(MODULES.REPORT_PURCHASE_REG, 'view') || fin;
    if (item.path === '/reports/gst-purchase')
      return hasPermission(MODULES.REPORT_GST_PURCHASE, 'view') || fin;
    if (item.path === '/reports/sales-register')
      return hasPermission(MODULES.REPORT_SALES_REG, 'view') || fin;
    if (item.path === '/reports/gst-sales')
      return hasPermission(MODULES.REPORT_GST_SALES, 'view') || fin;
    if (item.path === '/reports/outstanding')
      return hasPermission(MODULES.REPORT_OUTSTANDING, 'view') || fin;
    if (item.path === '/reports/day-book')
      return hasPermission(MODULES.REPORT_DAY_BOOK, 'view') || fin;
    if (item.path === '/reports/party-ledger')
      return hasPermission(MODULES.REPORT_PARTY_LEDGER, 'view') || fin;
    if (
      item.path === '/reports/manufacturing' ||
      item.path === '/reports/scrap' ||
      item.path === '/reports/financial' ||
      item.path === '/reports/gst' ||
      item.path === '/reports/dashboard' ||
      item.path === '/reports/ai-forecasting'
    )
      return true;
    return false;
  });

  const visiblePrintItems = printGroup.items.filter((item) => {
    if (item.path === '/print/barcode-management')
      return hasPermission(MODULES.BARCODE_PRINT, 'view');
    return false;
  });

  const visibleManufacturingItems = manufacturingGroup.items.filter((item) => {
    if (item.path === '/manufacturing/my-work-orders') return true;
    if (item.path === '/masters/routing') {
      return hasPermission(MODULES.ROUTING, 'view') || hasPermission(MODULES.PURCHASE_PAYMENT, 'view');
    }
    return hasPermission(MODULES.PURCHASE_PAYMENT, 'view');
  });

  const visibleFinanceItems = financeGroup.items.filter((item) => {
    if (item.path === '/finance/accounts')
      return hasPermission(MODULES.CHART_OF_ACCOUNTS, 'view');
    if (item.path === '/finance/journal')
      return hasPermission(MODULES.JOURNAL_VOUCHER, 'view');
    if (item.path === '/finance/trial-balance')
      return hasPermission(MODULES.TRIAL_BALANCE, 'view');
    if (item.path === '/finance/ledger')
      return hasPermission(MODULES.ACCOUNT_LEDGER, 'view');
    const fin = hasControl('viewFinancialReports');
    if (item.path === '/finance/reports/profit-loss')
      return hasPermission(MODULES.PROFIT_LOSS, 'view') || fin;
    if (item.path === '/finance/reports/balance-sheet')
      return hasPermission(MODULES.BALANCE_SHEET, 'view') || fin;
    if (item.path === '/finance/bank-reconciliation')
      return hasPermission(MODULES.BANK_RECONCILIATION, 'view');
    if (item.path === '/finance/assets')
      return hasPermission(MODULES.FIXED_ASSET, 'view');
    if (item.path === '/finance/currencies')
      return hasPermission(MODULES.CURRENCY, 'view');
    return false;
  });

  const canSeeMasters = visibleMastersItems.length > 0;
  const canSeeSales = visibleSalesItems.length > 0;
  const canSeePurchase = visiblePurchaseItems.length > 0;
  const canSeeInventory = visibleInventoryItems.length > 0;
  const canSeeCompliance = visibleComplianceItems.length > 0;
  const canSeePrint = visiblePrintItems.length > 0;
  const canSeeManufacturing = visibleManufacturingItems.length > 0;
  const canSeeFinance = visibleFinanceItems.length > 0;
  const canSeeReports = visibleReportsItems.length > 0;
  const canSeeUsers =
    hasPermission(MODULES.USERS, 'view') || hasControl('manageUserPermissions');
  const canSeeSettings =
    hasPermission(MODULES.SETTINGS, 'view') ||
    hasControl('manageUserPermissions');

  const visibleMastersGroup = { ...mastersGroup, items: visibleMastersItems };
  const visibleSalesGroup = { ...salesGroup, items: visibleSalesItems };
  const visiblePurchaseGroup = {
    ...purchaseGroup,
    items: visiblePurchaseItems,
  };
  const visibleInventoryGroup = {
    ...inventoryGroup,
    items: visibleInventoryItems,
  };
  const visibleComplianceGroup = {
    ...complianceGroup,
    items: visibleComplianceItems,
  };
  const visibleReportsGroup = { ...reportsGroup, items: visibleReportsItems };
  const visiblePrintGroup = { ...printGroup, items: visiblePrintItems };
  const visibleManufacturingGroup = {
    ...manufacturingGroup,
    items: visibleManufacturingItems,
  };
  const visibleFinanceGroup = {
    ...financeGroup,
    items: visibleFinanceItems,
  };

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleLogout = async () => {
    setLogoutConfirm(false);
    try {
      await postData('api/v1/auth/logout', {});
    } catch (err) {
      console.error('Logout API error:', err);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('auth_user');
      navigate('/login', { replace: true });
    }
  };

  // ── Sub-components ────────────────────────────────────────────────────────
  const NavBtn = ({ item }: { item: NavItem }) => {
    const active =
      item.path === '/'
        ? location.pathname === '/'
        : location.pathname.startsWith(item.path);
    return (
      <NavLink
        to={item.path}
        end={item.path === '/'}
        title={collapsed ? item.label : undefined}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150 cursor-pointer ${active
          ? 'bg-[#4f46e5] text-white'
          : 'text-slate-300 hover:bg-white/10 hover:text-white'
          }`}
      >
        <div className="w-5 h-5 flex items-center justify-center shrink-0">
          <i className={`${item.icon} text-lg leading-none`} />
        </div>
        {!collapsed && (
          <span className="text-sm font-medium whitespace-nowrap">
            {item.label}
          </span>
        )}
      </NavLink>
    );
  };

  const CollapseGroup = ({
    group,
    open,
    setOpen,
  }: {
    group: NavGroup;
    open: boolean;
    setOpen: (v: boolean) => void;
  }) => {
    const isGroupActive = location.pathname.startsWith(group.basePath);

    const [subOpen, setSubOpen] = useState<Record<string, boolean>>(() => {
      const initial: Record<string, boolean> = {};
      group.items.forEach((item) => {
        const isChildActive =
          location.pathname.startsWith(item.path) ||
          (item.items && item.items.some((sub) => location.pathname.startsWith(sub.path)));
        if (item.items && isChildActive) {
          initial[item.label] = true;
        }
      });
      return initial;
    });

    const toggleSub = (label: string) => {
      setSubOpen((prev) => ({ ...prev, [label]: !prev[label] }));
    };

    if (group.items.length === 0) return null;
    return (
      <div className="px-2 mt-1">
        <button
          onClick={() =>
            collapsed ? navigate(group.items[0].path) : setOpen(!open)
          }
          title={collapsed ? group.label : undefined}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all cursor-pointer ${isGroupActive
            ? 'bg-white/15 text-white'
            : 'text-slate-300 hover:bg-white/10 hover:text-white'
            }`}
        >
          <div className="w-5 h-5 flex items-center justify-center shrink-0">
            <i className={`${group.icon} text-lg leading-none`} />
          </div>
          {!collapsed && (
            <>
              <span className="text-sm font-medium whitespace-nowrap flex-1 text-left">
                {group.label}
              </span>
              <i
                className={`text-slate-400 text-sm transition-transform duration-200 ${open ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'}`}
              />
            </>
          )}
        </button>
        {!collapsed && open && (
          <ul className="mt-1 ml-4 border-l border-white/10 pl-3 space-y-0.5">
            {group.items.map((item) => {
              const hasSubItems = item.items && item.items.length > 0;
              const isSubOpen = !!subOpen[item.label];
              const isItemActive =
                location.pathname.startsWith(item.path) ||
                (item.items && item.items.some((sub) => location.pathname.startsWith(sub.path)));

              if (hasSubItems) {
                return (
                  <li key={item.path} className="space-y-0.5">
                    <button
                      onClick={() => toggleSub(item.label)}
                      className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-all text-sm cursor-pointer ${isItemActive
                        ? 'bg-white/5 text-white font-medium'
                        : 'text-slate-400 hover:bg-white/10 hover:text-white'
                        }`}
                    >
                      <div className="w-4 h-4 flex items-center justify-center shrink-0">
                        <i className={`${item.icon} text-sm leading-none`} />
                      </div>
                      <span className="whitespace-nowrap flex-1 text-left">{item.label}</span>
                      <i
                        className={`text-slate-400 text-xs transition-transform duration-200 ${isSubOpen ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'
                          }`}
                      />
                    </button>
                    {isSubOpen && (
                      <ul className="mt-0.5 ml-3 border-l border-white/10 pl-2.5 space-y-0.5">
                        {item.items!.map((subItem) => (
                          <li key={subItem.path}>
                            <NavLink
                              to={subItem.path}
                              className={({ isActive: a }) =>
                                `flex items-center gap-2 px-2 py-1.5 rounded-md transition-all text-xs cursor-pointer ${a
                                  ? 'bg-[#4f46e5] text-white font-medium'
                                  : 'text-slate-400 hover:bg-white/10 hover:text-white'
                                }`
                              }
                            >
                              <div className="w-3 h-3 flex items-center justify-center shrink-0">
                                <i className={`${subItem.icon || 'ri-circle-fill'} text-[8px] leading-none`} />
                              </div>
                              <span className="whitespace-nowrap">{subItem.label}</span>
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              }

              const isEInvoiceDisabled = item.path === '/compliance/e-invoice' && !eInvoiceEligible;

              if (isEInvoiceDisabled) {
                return (
                  <li key={item.path}>
                    <div
                      title="E-Invoice disabled: Previous FY turnover is below ₹5 Crore"
                      onClick={() => toast.info('E-Invoicing is disabled because company FY turnover is below ₹5 Crore.')}
                      className="flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg text-sm text-slate-500 bg-white/5 opacity-50 cursor-not-allowed select-none transition-all"
                    >
                      <div className="flex items-center gap-2.5">
                        <div className="w-4 h-4 flex items-center justify-center shrink-0">
                          <i className={`${item.icon} text-sm leading-none text-slate-500`} />
                        </div>
                        <span className="whitespace-nowrap">{item.label}</span>
                      </div>
                      <span className="text-[9px] bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded font-mono font-medium">
                        &lt; 5Cr
                      </span>
                    </div>
                  </li>
                );
              }

              return (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    className={({ isActive: a }) =>
                      `flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-all text-sm cursor-pointer ${a
                        ? 'bg-[#4f46e5] text-white font-medium'
                        : 'text-slate-400 hover:bg-white/10 hover:text-white'
                      }`
                    }
                  >
                    <div className="w-4 h-4 flex items-center justify-center shrink-0">
                      <i className={`${item.icon} text-sm leading-none`} />
                    </div>
                    <span className="whitespace-nowrap">{item.label}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  const SectionLabel = ({ label }: { label: string }) =>
    !collapsed ? (
      <p className="px-4 mt-4 mb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
        {label}
      </p>
    ) : null;

  const UserBottom = () => (
    <div className="border-t border-white/10 p-3 shrink-0">
      {collapsed ? (
        <button
          onClick={() => setLogoutConfirm(true)}
          title="Logout"
          className="w-8 h-8 flex items-center justify-center mx-auto rounded-full bg-[#4f46e5] text-xs font-bold text-white cursor-pointer hover:bg-indigo-600 transition-colors"
        >
          {userInitials}
        </button>
      ) : (
        <div className="space-y-1">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-8 h-8 rounded-full bg-[#4f46e5] flex items-center justify-center text-xs font-bold text-white shrink-0">
              {userInitials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-white whitespace-nowrap truncate">
                {user?.name ?? 'Admin User'}
              </p>
              <p className="text-[11px] text-slate-400 whitespace-nowrap">
                {roleLabel[role] ?? role}
              </p>
            </div>
          </div>
          <button
            onClick={() => setLogoutConfirm(true)}
            className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs text-slate-400 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
          >
            <i className="ri-logout-box-line text-sm" />
            <span>Sign out</span>
          </button>
        </div>
      )}
    </div>
  );

  // ── Logo / brand header ───────────────────────────────────────────────────
  const logoUrl =
    company?.logo_url ??
    'https://public.readdy.ai/ai/img_res/562db67e-18cd-4892-ada1-aa9c5633c1ae.png';

  const brandName = company?.name || (isGuard ? 'Guard Portal' : 'InvenPro');

  const BrandHeader = ({ guardMode = false }: { guardMode?: boolean }) => (
    <div className="flex items-center h-[60px] px-4 border-b border-white/10 shrink-0">
      <div className="w-8 h-8 flex items-center justify-center shrink-0">
        {guardMode ? (
          <i className="ri-shield-user-fill text-[#4f46e5] text-xl" />
        ) : (
          <img
            src={logoUrl}
            alt={brandName}
            className="w-8 h-8 object-contain rounded"
          />
        )}
      </div>
      {!collapsed && (
        <span className="ml-3 font-bold text-base tracking-tight whitespace-nowrap text-white truncate max-w-[140px]">
          {brandName}
        </span>
      )}
      <button
        onClick={onToggle}
        className="ml-auto w-6 h-6 flex items-center justify-center text-slate-400 hover:text-white transition-colors cursor-pointer shrink-0"
      >
        <i
          className={`${collapsed ? 'ri-menu-unfold-line' : 'ri-menu-fold-line'} text-base`}
        />
      </button>
    </div>
  );

  const asideClass =
    'flex flex-col h-screen bg-[#1e293b] text-white transition-all duration-300 ease-in-out shrink-0';

  // ── Guard sidebar ─────────────────────────────────────────────────────────
  if (isGuard) {
    return (
      <>
        <aside className={asideClass} style={{ width: collapsed ? 64 : 240 }}>
          <BrandHeader guardMode />
          <nav ref={navRef} onScroll={handleNavScroll} className="flex-1 py-4 space-y-0.5 px-2 overflow-y-auto">
            <SectionLabel label="Gate Pass" />
            {guardNavItems.map((item) => (
              <NavBtn key={item.path} item={item} />
            ))}
          </nav>
          <UserBottom />
        </aside>
        <SignOutConfirmModal
          open={logoutConfirm}
          onConfirm={handleLogout}
          onCancel={() => setLogoutConfirm(false)}
        />
      </>
    );
  }

  // ── Standard sidebar ──────────────────────────────────────────────────────
  return (
    <>
      <aside className={asideClass} style={{ width: collapsed ? 64 : 240 }}>
        <BrandHeader />

        <nav ref={navRef} onScroll={handleNavScroll} className="flex-1 py-4 overflow-y-auto overflow-x-hidden scrollbar-thin">
          <SectionLabel label="Main" />
          <ul className="space-y-0.5 px-2">
            <li>
              <NavLink
                to="/"
                end
                className={({ isActive: a }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all cursor-pointer ${a
                    ? 'bg-[#4f46e5] text-white'
                    : 'text-slate-300 hover:bg-white/10 hover:text-white'
                  }`
                }
                title={collapsed ? 'Dashboard' : undefined}
              >
                <div className="w-5 h-5 flex items-center justify-center shrink-0">
                  <i className="ri-dashboard-3-line text-lg leading-none" />
                </div>
                {!collapsed && (
                  <span className="text-sm font-medium whitespace-nowrap">
                    Dashboard
                  </span>
                )}
              </NavLink>
            </li>
          </ul>

          {canSeeMasters && (
            <>
              <SectionLabel label="Masters" />
              <CollapseGroup
                group={visibleMastersGroup}
                open={mastersOpen}
                setOpen={setMastersOpen}
              />
            </>
          )}

          {(canSeeSales || canSeePurchase || canSeeInventory) && (
            <SectionLabel label="Transactions" />
          )}
          {canSeeSales && (
            <CollapseGroup
              group={visibleSalesGroup}
              open={salesOpen}
              setOpen={setSalesOpen}
            />
          )}
          {canSeePurchase && (
            <CollapseGroup
              group={visiblePurchaseGroup}
              open={purchaseOpen}
              setOpen={setPurchaseOpen}
            />
          )}
          {canSeeInventory && (
            <CollapseGroup
              group={visibleInventoryGroup}
              open={inventoryOpen}
              setOpen={setInventoryOpen}
            />
          )}

          {canSeeCompliance && (
            <>
              <SectionLabel label="Compliance" />
              <CollapseGroup
                group={visibleComplianceGroup}
                open={complianceOpen}
                setOpen={setComplianceOpen}
              />
            </>
          )}

          {canSeePrint && (
            <>
              <SectionLabel label="Print" />
              <CollapseGroup
                group={visiblePrintGroup}
                open={printOpen}
                setOpen={setPrintOpen}
              />
            </>
          )}

          {canSeeManufacturing && (
            <>
              <SectionLabel label="Manufacturing" />
              <CollapseGroup
                group={visibleManufacturingGroup}
                open={manufacturingOpen}
                setOpen={setManufacturingOpen}
              />
            </>
          )}

          {canSeeFinance && (
            <>
              <SectionLabel label="Finance" />
              <CollapseGroup
                group={visibleFinanceGroup}
                open={financeOpen}
                setOpen={setFinanceOpen}
              />
            </>
          )}

          {canSeeReports && (
            <>
              <SectionLabel label="Reports" />
              <CollapseGroup
                group={visibleReportsGroup}
                open={reportsOpen}
                setOpen={setReportsOpen}
              />
            </>
          )}

          {(canSeeUsers || canSeeSettings) && (
            <>
              <SectionLabel label="System" />
              <ul className="space-y-0.5 px-2 mt-1">
                {canSeeUsers && (
                  <li>
                    <NavBtn
                      item={{
                        label: 'Users',
                        path: '/users',
                        icon: 'ri-team-line',
                      }}
                    />
                  </li>
                )}
                {canSeeSettings && (
                  <li>
                    <NavBtn
                      item={{
                        label: 'Settings',
                        path: '/settings',
                        icon: 'ri-settings-3-line',
                      }}
                    />
                  </li>
                )}
              </ul>
            </>
          )}
        </nav>

        <UserBottom />
      </aside>
      <SignOutConfirmModal
        open={logoutConfirm}
        onConfirm={handleLogout}
        onCancel={() => setLogoutConfirm(false)}
      />
    </>
  );
}
